import ApiError from '~/types/ApiError';

import { json_headers, request } from './common';

type ZendeskRuntimeConfig = {
  ZENDESK_SUBDOMAIN?: string;
};

function getRuntimeConfig(): typeof window.configs & ZendeskRuntimeConfig {
  return window.configs as typeof window.configs & ZendeskRuntimeConfig;
}

function getBaseTicketPath(): string {
  return `${window.configs.API_HOST}/api/zendesk/ticket`;
}

const adminOrderBrandToZendeskBrand = {
  luxuryescapes: 'luxuryescapes',
  scoopontravel: 'scoopon',
  cudotravel: 'cudo',
  dealstravel: 'deals',
  treatmetravel: 'treatme',
  lebusinesstraveller: 'lebt',
  leagenthub: 'leagenthub',
  leagenthub_cvfr: 'leagenthub',
} as const;

export const BEDBANK_BACK_OF_HOUSE_ISSUE_TYPES = [
  'External Supplier Mapping / Content Issue',
  'Failed Booking',
  'Post Mapping Issue',
  'Prior Mapping Issue',
] as const;

type AdminOrderBrand = keyof typeof adminOrderBrandToZendeskBrand;

export type ZendeskOrderItemTicketBrand = (typeof adminOrderBrandToZendeskBrand)[AdminOrderBrand];
export type ZendeskOrderItemTicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type ZendeskBackOfHouseMappingIssueType = (typeof BEDBANK_BACK_OF_HOUSE_ISSUE_TYPES)[number];

export type OrderItemTicket = {
  ticketId: string;
};

export type CreateBackOfHouseMappingIssuePayload = {
  orderId: string;
  orderItemId: string;
  customerEmail: string;
  submitterId: string;
  brand?: ZendeskOrderItemTicketBrand;
  type: ZendeskBackOfHouseMappingIssueType;
  priority?: ZendeskOrderItemTicketPriority;
  notes?: string;
};

export type CreateBackOfHouseMappingIssueResponse = {
  status: number;
  message: string;
  result: OrderItemTicket;
};

export function mapAdminOrderBrandToZendeskBrand(brand?: string): ZendeskOrderItemTicketBrand | undefined {
  if (!brand) {
    return undefined;
  }

  return adminOrderBrandToZendeskBrand[brand as AdminOrderBrand];
}

export async function getOrderItemTicket(orderItemId: string): Promise<OrderItemTicket | null> {
  const url = `${getBaseTicketPath()}/order-item-ticket/${orderItemId}`;

  try {
    const response = (await request(url, {
      method: 'GET',
      headers: json_headers,
    })) as { result: OrderItemTicket };

    return response.result;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }

    throw error;
  }
}

export async function createBackOfHouseMappingIssue(
  payload: CreateBackOfHouseMappingIssuePayload,
): Promise<CreateBackOfHouseMappingIssueResponse> {
  const url = `${getBaseTicketPath()}/boh-mapping-issue`;

  return (await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  })) as CreateBackOfHouseMappingIssueResponse;
}

export function getZendeskTicketUrl(ticketId: string): string | null {
  const subdomain = getRuntimeConfig().ZENDESK_SUBDOMAIN?.trim();

  if (!subdomain) {
    return null;
  }

  return `https://${subdomain}.zendesk.com/agent/tickets/${ticketId}`;
}
